import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { VStack, Input, Link } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import Popover from "@hydrologiq/shared/chakra/Popover"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { showSuccessToast, showErrorToast } from "@hydrologiq/shared/utils/toast"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { BatchItem } from "./BatchCombobox"

// Standalone form for use in dialogs (similar to CustomerCreationForm)
export interface BatchCreationFormProps {
  onSuccess: (batch: BatchItem) => void
  onCancel?: () => void
  onBack?: () => void
  defaultName?: string
}

export const BatchCreationForm = ({ onSuccess, onCancel, onBack, defaultName = "" }: BatchCreationFormProps) => {
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const [createBatch, { loading }] = useMutationWithEndpoint(
    gqlTypes.CreateBatchDocument,
    {
      // Update Apollo cache to add the new batch to GetBatches query
      update: (cache, { data }) => {
        if (data?.createBatch) {
          // Read the existing query from cache
          const existingData = cache.readQuery({ query: gqlTypes.GetBatchesDocument })

          if (existingData?.batches) {
            let updatedBatches: any

            // Handle both response formats: paginated (BatchesResponse) and simple array
            if (Array.isArray(existingData.batches)) {
              // Simple array format (backwards compatibility)
              updatedBatches = [data.createBatch, ...existingData.batches]
            } else if (existingData.batches.data && Array.isArray(existingData.batches.data)) {
              // Paginated format (BatchesResponse)
              updatedBatches = {
                data: [data.createBatch, ...existingData.batches.data],
                meta: {
                  ...existingData.batches.meta,
                  total_count: (existingData.batches.meta?.total_count || 0) + 1,
                },
              }
            } else {
              return
            }

            // Write back to cache with the new batch prepended
            cache.writeQuery({
              query: gqlTypes.GetBatchesDocument,
              data: {
                batches: updatedBatches as any,
              },
            })
          }
        }
      },
    },
    "primary"
  )

  const handleSubmit = async (formData: FormData) => {
    try {
      setError(null)
      const nameFromForm = formData.get("name") as string
      const finalName = nameFromForm.trim() || defaultName.trim()

      const variables: gqlTypes.CreateBatchMutationVariables = finalName ? { name: finalName } : {}

      const { data } = await createBatch({
        variables,
      })

      if (data?.createBatch) {
        const newBatch = data.createBatch as BatchItem

        // Invalidate React Query cache for batches to ensure fresh data in SearchCombobox
        queryClient.invalidateQueries({ queryKey: ["batches"] })

        showSuccessToast("Batch Created", `Batch "${newBatch.name}" has been successfully created.`)
        setError(null)
        onSuccess(newBatch)
        return true
      } else {
        const errorMessage = "Failed to create batch. Please try again."
        setError(errorMessage)
        showErrorToast("Creation Failed", errorMessage)
        return false
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred while creating the batch"
      setError(errorMessage)
      showErrorToast("Creation Failed", errorMessage)
      return false
    }
  }

  return (
    <Form
      helperText="Create a new batch. Leave name empty for auto-generated numbering."
      actionType="create"
      loading={loading}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onBack={onBack}
      useFormTag={false}
    >
      <VStack gap={4} width="100%">
        {error && <ErrorAlert title="Batch Creation Error" message={error} onClose={() => setError(null)} />}
        <Field label="Batch name" width="100%">
          <Input
            name="name"
            placeholder="Leave empty for auto-generated name"
            defaultValue={defaultName.trim()}
            autoFocus
          />
        </Field>
      </VStack>
    </Form>
  )
}

interface BatchCreateButtonProps {
  onBatchCreated?: (batch: BatchItem) => void
  defaultName?: string
}

const BatchCreateButton = ({ onBatchCreated, defaultName = "" }: BatchCreateButtonProps) => {
  const [open, setOpen] = useState(false)

  const handleSuccess = (batch: BatchItem) => {
    setOpen(false)
    onBatchCreated?.(batch)
  }

  const handleCancel = () => {
    setOpen(false)
  }

  return (
    <Popover
      actionVariant="primary"
      placement="top"
      open={open}
      onOpenChange={setOpen}
      size="xs"
      additionalContent={
        <Link variant="primary" whiteSpace="nowrap">
          Create new batch
        </Link>
      }
    >
      <BatchCreationForm onSuccess={handleSuccess} onCancel={handleCancel} defaultName={defaultName} />
    </Popover>
  )
}

export default BatchCreateButton
